import React, { useState, useRef, useEffect, useCallback } from "react";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";

interface FloatingChatButtonProps {
  onOpen: () => void;
}

const FloatingChatButton: React.FC<FloatingChatButtonProps> = ({ onOpen }) => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const getOffset = useCallback(() => {
    const width = document.documentElement.clientWidth;
    const size =
      width >= 1024 ? 64 : width >= 768 ? 56 : width >= 640 ? 48 : 40;
    return size + 2;
  }, []);

  const [position, setPosition] = useState({
    x: document.documentElement.clientWidth - getOffset(),
    y: document.documentElement.clientHeight - getOffset(),
  });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [unreadCount, setUnreadCount] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Fetch unread count
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000); // Check every 30 seconds
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get("/messages/unread-count");
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  // Load saved position from localStorage
  useEffect(() => {
    const savedPosition = localStorage.getItem("chatButtonPosition");
    const offset = getOffset();
    if (savedPosition) {
      const { x, y } = JSON.parse(savedPosition);
      // For students: always lock to right edge and bottom
      const validX = isAdmin
        ? Math.min(
            Math.max(0, x),
            document.documentElement.clientWidth - offset,
          )
        : document.documentElement.clientWidth - offset;
      const validY = isAdmin
        ? Math.min(
            Math.max(0, y),
            document.documentElement.clientHeight - offset,
          )
        : document.documentElement.clientHeight - offset;
      setPosition({ x: validX, y: validY });
    } else {
      // Default position: bottom right
      setPosition({
        x: document.documentElement.clientWidth - offset,
        y: document.documentElement.clientHeight - offset,
      });
    }
  }, [isAdmin, getOffset]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const offset = getOffset();
      setPosition({
        x: isAdmin
          ? Math.min(position.x, document.documentElement.clientWidth - offset)
          : document.documentElement.clientWidth - offset,
        y: document.documentElement.clientHeight - offset,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isAdmin, position.x, getOffset]); // Added position.x and getOffset to dependencies

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const offset = getOffset();
      if (isDragging && !isAdmin) {
        // For students: lock to right edge and bottom
        setPosition({
          x: document.documentElement.clientWidth - offset,
          y: document.documentElement.clientHeight - offset,
        });
      } else if (isDragging && isAdmin) {
        // For admin: allow full movement
        const newX = Math.min(
          Math.max(0, e.clientX - dragStart.x),
          document.documentElement.clientWidth - offset,
        );
        const newY = Math.min(
          Math.max(0, e.clientY - dragStart.y),
          document.documentElement.clientHeight - offset,
        );
        setPosition({ x: newX, y: newY });
      }
    },
    [isDragging, isAdmin, dragStart, getOffset],
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      // Save position to localStorage
      localStorage.setItem("chatButtonPosition", JSON.stringify(position));
    }
  }, [isDragging, position]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isAdmin) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    } else if (isAdmin) {
      // Admin can still drag freely
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragStart, handleMouseMove, handleMouseUp]);

  const handleClick = () => {
    if (!isDragging) {
      if (isAdmin) {
        // For admin, navigate to messages page
        navigate("/admin/chat");
      } else {
        // For students, open chat popup
        onOpen();
        // Reset unread count when opening chat
        setUnreadCount(0);
      }
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isAdmin) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y,
      });
    } else if (isAdmin) {
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y,
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const offset = getOffset();
    if (isDragging && !isAdmin) {
      // For students: lock to right edge and bottom
      setPosition({
        x: document.documentElement.clientWidth - offset,
        y: document.documentElement.clientHeight - offset,
      });
    } else if (isDragging && isAdmin) {
      const touch = e.touches[0];
      // For admin: allow full movement
      const newX = Math.min(
        Math.max(0, touch.clientX - dragStart.x),
        document.documentElement.clientWidth - offset,
      );
      const newY = Math.min(
        Math.max(0, touch.clientY - dragStart.y),
        document.documentElement.clientHeight - offset,
      );
      setPosition({ x: newX, y: newY });
    }
  };

  const handleTouchEnd = () => {
    if (isDragging) {
      setIsDragging(false);
      localStorage.setItem("chatButtonPosition", JSON.stringify(position));
    }
  };

  // For admin, only show if there are unread messages
  if (isAdmin && unreadCount === 0) {
    return null;
  }

  return (
    <button
      ref={buttonRef}
      className={`fixed z-40 w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-gradient-to-r from-indigo-500 via-purple-500 to-purple-600 hover:from-indigo-600 hover:via-purple-600 hover:to-purple-700 text-white rounded-full shadow-lg hover:shadow-2xl flex items-center justify-center transition-all duration-300 transform backdrop-blur-sm border border-white/20 ${
        isDragging
          ? "cursor-grabbing scale-110"
          : isAdmin
            ? "cursor-pointer hover:scale-105"
            : "cursor-grab hover:scale-105"
      } ${isAdmin ? "animate-pulse" : ""}`}
      style={{
        left: !isAdmin && !isDragging ? "auto" : `${position.x}px`,
        right: !isAdmin && !isDragging ? "2px" : "auto",
        top: !isAdmin && !isDragging ? "auto" : `${position.y}px`,
        bottom: !isAdmin && !isDragging ? "2px" : "auto",
        touchAction: "none",
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={handleClick}
      title={isAdmin ? "View student messages" : "Chat with teacher"}
    >
      <ChatBubbleLeftRightIcon className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7" />
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 sm:-top-1.5 sm:-right-1.5 md:-top-2 md:-right-2 bg-red-500 text-white text-xs font-bold rounded-full h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-5 lg:h-6 lg:w-6 flex items-center justify-center text-[8px] sm:text-[10px] md:text-xs">
          {unreadCount > 99 ? "99+" : unreadCount}
        </span>
      )}
    </button>
  );
};

export default FloatingChatButton;
